// notificationService.js — In-app notification service
import { supabase, supabaseUrl, isSupabaseConfigured } from '../supabaseClient';

/**
 * Fire-and-forget: send push notification via Supabase Edge Function.
 * This runs in the background — failures don't block the notification UI.
 * Works even when the employee's browser tab is closed.
 */
const sendPushToUser = async (userId, title, message, link) => {
  try {
    if (!supabaseUrl || supabaseUrl === 'YOUR_SUPABASE_URL') return;

    // SECURITY (AUDIT F5): send-push now requires a caller JWT (any signed-in
    // user may notify another user; link is same-origin restricted server-side).
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    const response = await fetch(`${supabaseUrl}/functions/v1/send-push`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ userId, title, message, link }),
    });

    if (!response.ok) {
      console.warn('[Push] Edge Function returned', response.status);
    }
  } catch (err) {
    console.warn('[Push] Failed to call send-push Edge Function:', err.message);
  }
};

/**
 * Create a notification for a user
 */
export const createNotification = async ({
  userId,
  type,
  title,
  message,
  link,
  relatedTable,
  relatedRecordId,
  companyId,
}) => {
  console.log('[NotificationService] === createNotification called ===');
  console.log('[NotificationService] params:', { userId, type, title, message: message?.substring(0, 50), link, relatedTable, relatedRecordId, companyId });

  if (!isSupabaseConfigured()) {
    console.error('[NotificationService] Supabase not configured!');
    return { success: false, error: 'Supabase not configured' };
  }

  if (!userId) {
    console.error('[NotificationService] Missing userId!');
    return { success: false, error: 'Missing userId' };
  }

  try {
    const { error } = await supabase.from('notifications').insert([{
      user_id: userId,
      type,
      title,
      message,
      link,
      related_table: relatedTable,
      related_record_id: relatedRecordId,
      company_id: companyId || null,
    }]);

    if (error) {
      console.error('[NotificationService] Insert error:', error);
      throw error;
    }
    console.log('[NotificationService] Notification CREATED SUCCESSFULLY for user:', userId);
    // Fire-and-forget: send push notification via Edge Function (works even with tab closed)
    sendPushToUser(userId, title, message, link).catch(() => {});
    return { success: true, error: null };
  } catch (err) {
    console.error('[NotificationService] Failed to create notification:', err);
    return { success: false, error: err.message };
  }
};

/**
 * Create or update a notification in place (dedupe).
 * Dedupe key: (user_id + type + related_record_id) — i.e. recurrences of the
 * SAME underlying entity (e.g. document_123 expiring again tomorrow).
 * A new occurrence updates the existing row's message and timestamp in place
 * and marks it unread again, instead of inserting a new row.
 */
export const upsertNotification = async ({
  userId,
  type,
  title,
  message,
  link,
  relatedTable,
  relatedRecordId,
  companyId,
}) => {
  if (!isSupabaseConfigured()) {
    return { success: false, error: 'Supabase not configured' };
  }
  if (!userId) {
    return { success: false, error: 'Missing userId' };
  }

  try {
    let query = supabase
      .from('notifications')
      .select('id')
      .eq('user_id', userId)
      .eq('type', type)
      .order('created_at', { ascending: false })
      .limit(1);

    if (relatedRecordId) {
      query = query.eq('related_record_id', relatedRecordId);
    }

    const { data: existing, error: findError } = await query;
    if (findError) throw findError;

    if (existing && existing.length > 0) {
      const { error } = await supabase
        .from('notifications')
        .update({
          title,
          message,
          link,
          is_read: false,
          read_at: null,
          created_at: new Date().toISOString(),
          ...(companyId ? { company_id: companyId } : {}),
        })
        .eq('id', existing[0].id);

      if (error) throw error;
      return { success: true, updated: true, error: null };
    }

    return createNotification({ userId, type, title, message, link, relatedTable, relatedRecordId, companyId });
  } catch (err) {
    console.error('[NotificationService] Failed to upsert notification:', err);
    return { success: false, error: err.message };
  }
};

/**
 * Get unread notifications count for a user.
 * Pass companyId to count only the selected company's notifications
 * ('all' / null / undefined = no company filter).
 */
export const getUnreadCount = async (userId, companyId, sinceIso = null) => {
  if (!isSupabaseConfigured() || !userId) return 0;
  if (!navigator.onLine) return 0; // Skip when offline

  try {
    let query = supabase
      .from('notifications')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId)
      .eq('is_read', false)
      .retry(false);

    if (companyId && companyId !== 'all') {
      query = query.eq('company_id', companyId);
    }

    // Optional "seen" cutoff: count only notifications created after the last
    // time the bell popover was opened (badge = unseen, not unread).
    if (sinceIso) {
      query = query.gt('created_at', sinceIso);
    }

    const { count, error } = await query;

    if (error) throw error;
    return count || 0;
  } catch (err) {
    console.error('[NotificationService] Error getting unread count:', err);
    return 0;
  }
};

/**
 * Get notifications for a user.
 * Pass companyId to only return the selected company's notifications
 * ('all' / null / undefined = no company filter).
 */
export const getNotifications = async (userId, { limit = 50, offset = 0, unreadOnly = false, companyId } = {}) => {
  if (!isSupabaseConfigured() || !userId) {
    return { success: false, data: [], count: 0, error: 'Not configured' };
  }

  if (!navigator.onLine) return { success: true, data: [], count: 0, error: null };

  try {
    let query = supabase
      .from('notifications')
      .select('*', { count: 'exact' })
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1)
      .retry(false);

    if (unreadOnly) {
      query = query.eq('is_read', false);
    }

    if (companyId && companyId !== 'all') {
      query = query.eq('company_id', companyId);
    }

    const { data, error, count } = await query;
    if (error) throw error;

    return { success: true, data: data || [], count: count || 0, error: null };
  } catch (err) {
    console.error('[NotificationService] Error fetching notifications:', err);
    return { success: false, data: [], count: 0, error: err.message };
  }
};

/**
 * Mark a notification as read
 */
export const markAsRead = async (notificationId) => {
  if (!isSupabaseConfigured()) return { success: false, error: 'Supabase not configured' };

  try {
    const { error } = await supabase
      .from('notifications')
      .update({ is_read: true, read_at: new Date().toISOString() })
      .eq('id', notificationId);

    if (error) throw error;
    return { success: true, error: null };
  } catch (err) {
    console.error('[NotificationService] Error marking as read:', err);
    return { success: false, error: err.message };
  }
};

/**
 * Mark all notifications as read for a user
 */
export const markAllAsRead = async (userId) => {
  if (!isSupabaseConfigured()) return { success: false, error: 'Supabase not configured' };

  try {
    const { error } = await supabase
      .from('notifications')
      .update({ is_read: true, read_at: new Date().toISOString() })
      .eq('user_id', userId)
      .eq('is_read', false);

    if (error) throw error;
    return { success: true, error: null };
  } catch (err) {
    console.error('[NotificationService] Error marking all as read:', err);
    return { success: false, error: err.message };
  }
};

/**
 * Fire-and-forget: record that the user has SEEN their notifications (the bell
 * popover was opened). Distinct from markAllAsRead - items stay unread (blue
 * dot) until clicked; this only stores when the badge was last cleared so
 * other devices agree on the badge count.
 * Requires backend-functions/mark_notifications_seen.sql (degrades gracefully
 * to a debug log when the RPC has not been installed yet).
 */
export const markNotificationsSeen = async () => {
  if (!isSupabaseConfigured()) return { success: false, error: 'Supabase not configured' };
  try {
    const { error } = await supabase.rpc('mark_notifications_seen');
    if (error) {
      console.debug(
        '[NotificationService] mark_notifications_seen RPC unavailable (run backend-functions/mark_notifications_seen.sql):',
        error.message
      );
      return { success: false, error: error.message };
    }
    return { success: true, error: null };
  } catch (err) {
    console.debug('[NotificationService] mark_notifications_seen failed:', err?.message);
    return { success: false, error: err?.message };
  }
};

/**
 * Mark all related approval notifications as read (for other admins/managers)
 * Uses RPC to bypass RLS
 */
export const markRelatedNotificationsAsRead = async (relatedRecordId, notificationType = 'approval_needed') => {
  if (!isSupabaseConfigured() || !relatedRecordId) return { success: false, error: 'Missing params' };

  try {
    const { error } = await supabase
      .rpc('mark_related_notifications_as_read', {
        p_related_record_id: relatedRecordId,
        p_notification_type: notificationType,
      });

    if (error) throw error;
    console.log('[NotificationService] Marked related notifications as read for record:', relatedRecordId);
    return { success: true, error: null };
  } catch (err) {
    console.error('[NotificationService] Error marking related notifications as read:', err);
    return { success: false, error: err.message };
  }
};

/**
 * Delete notifications tied to a record (resolved or removed).
 * Pass a notificationType (e.g. 'approval_needed') to only remove that kind,
 * or omit it to remove every notification referencing the record.
 * Uses RPC to bypass RLS (deletes other users' notifications too).
 */
export const deleteRelatedNotifications = async (relatedRecordId, notificationType = null) => {
  if (!isSupabaseConfigured() || !relatedRecordId) return { success: false, error: 'Missing params' };

  console.log('[NotificationService] deleteRelatedNotifications ->', { relatedRecordId, notificationType });

  try {
    const { data, error } = await supabase
      .rpc('delete_related_notifications', {
        p_related_record_id: String(relatedRecordId),
        p_notification_type: notificationType,
      });

    console.log('[NotificationService] deleteRelatedNotifications result ->', { data, error });
    if (error) throw error;
    return { success: true, error: null };
  } catch (err) {
    console.error('[NotificationService] Error deleting related notifications:', err);
    return { success: false, error: err.message };
  }
};

/**
 * Delete a notification
 */
export const deleteNotification = async (notificationId) => {
  if (!isSupabaseConfigured()) return { success: false, error: 'Supabase not configured' };

  try {
    const { error } = await supabase
      .from('notifications')
      .delete()
      .eq('id', notificationId);

    if (error) throw error;
    return { success: true, error: null };
  } catch (err) {
    console.error('[NotificationService] Error deleting notification:', err);
    return { success: false, error: err.message };
  }
};

/**
 * Get user_id by employee_id from user_profiles
 * Uses RPC to bypass RLS, with fallback to direct query
 *
 * user_profiles.employee_id is UUID type, matching employees.id
 */
export const getUserIdByEmployeeId = async (employeeId) => {
  if (!isSupabaseConfigured() || !employeeId) {
    console.log('[NotificationService] getUserIdByEmployeeId called with invalid input:', employeeId);
    return null;
  }

  console.log('[NotificationService] === START getUserIdByEmployeeId ===');
  console.log('[NotificationService] Input employeeId:', employeeId, 'type:', typeof employeeId);

  // Try 1: RPC function (bypasses RLS)
  try {
    console.log('[NotificationService] Trying RPC get_user_id_by_employee_id...');
    const { data, error } = await supabase
      .rpc('get_user_id_by_employee_id', { p_employee_id: employeeId });

    if (error) {
      console.error('[NotificationService] RPC error (function may not exist):', error.message, error.code);
    } else {
      console.log('[NotificationService] RPC raw response:', data);
      const userId = data?.[0]?.user_id;
      if (userId) {
        console.log('[NotificationService] Found user via RPC:', userId);
        console.log('[NotificationService] === END getUserIdByEmployeeId ===');
        return userId;
      }
      console.log('[NotificationService] RPC returned empty');
    }
  } catch (err) {
    console.error('[NotificationService] RPC exception:', err);
  }

  // Try 2: Direct query fallback (may fail due to RLS)
  // user_profiles.employee_id is UUID type, so we match directly
  try {
    console.log('[NotificationService] Fallback: direct query on user_profiles.employee_id (UUID)...');
    const { data, error } = await supabase
      .from('user_profiles')
      .select('id')
      .eq('employee_id', employeeId)
      .maybeSingle();

    if (error) {
      console.error('[NotificationService] Direct query error:', error.message);
    } else if (data?.id) {
      console.log('[NotificationService] Found user via direct UUID match:', data.id);
      console.log('[NotificationService] === END getUserIdByEmployeeId ===');
      return data.id;
    } else {
      console.log('[NotificationService] Direct query returned empty (likely RLS)');
    }
  } catch (err) {
    console.error('[NotificationService] Direct query exception:', err);
  }

  console.log('[NotificationService] ALL METHODS FAILED for employeeId:', employeeId);
  console.log('[NotificationService] === END getUserIdByEmployeeId ===');
  return null;
};

/**
 * Get all admin and manager user IDs for a company
 * Uses RPC function to bypass RLS
 */
export const getCompanyAdminsAndManagers = async (companyId) => {
  if (!isSupabaseConfigured() || !companyId) {
    console.log('[NotificationService] missing companyId');
    return [];
  }

  try {
    console.log('[NotificationService] Calling RPC get_company_admins for:', companyId);
    const { data, error } = await supabase
      .rpc('get_company_admins', { company_id: companyId });

    if (error) {
      console.error('[NotificationService] RPC error:', error);
      return [];
    }

    const admins = (data || []).map(row => row.user_id || row);
    console.log('[NotificationService] RPC returned admins:', admins.length, admins);
    return admins;
  } catch (err) {
    console.error('[NotificationService] Error:', err);
    return [];
  }
};

/**
 * Get auth user IDs of an employee's assigned CROSS-COMPANY managers
 * (cross_company_team_members — managers whose employee record lives in a
 * different company). They are valid leave/OT approvers for this employee
 * (see backend-functions/cross_company_team_members.sql).
 * @param {string} employeeId - employees.id (UUID) of the requester
 * @returns {Promise<string[]>} Array of user_profile IDs
 */
export const getCrossCompanyManagerUserIds = async (employeeId) => {
  if (!isSupabaseConfigured() || !employeeId) {
    return [];
  }

  try {
    const { data, error } = await supabase
      .rpc('get_cross_company_manager_user_ids', { p_employee_id: employeeId });

    if (error) {
      console.error('[NotificationService] RPC error (cross-company managers):', error);
      return [];
    }

    return (data || []).map(row => row.user_id || row);
  } catch (err) {
    console.error('[NotificationService] Error:', err);
    return [];
  }
};

/**
 * Subscribe to real-time notifications
 */
export const subscribeToNotifications = (userId, callback) => {
  if (!isSupabaseConfigured() || !userId) return null;

  const subscription = supabase
    .channel('notifications')
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${userId}`,
      },
      (payload) => {
        callback(payload.new);
      }
    )
    .subscribe((status) => {
      console.log('[NotificationService] Subscription status:', status);
    });

  return subscription;
};
